import React, { useMemo, useState } from 'react';
import {
  Badge, Box, Button, HStack, IconButton, Menu, MenuButton, MenuDivider, MenuItem, MenuList,
  Portal, Select, Text, Tooltip, useDisclosure, useToast,
} from '@chakra-ui/react';
import {
  FiCheckCircle, FiCopy, FiDownload, FiEdit2, FiEye, FiFileText, FiFolder, FiMoreVertical, FiRefreshCw, FiUsers, FiExternalLink,
} from 'react-icons/fi';
import axios from 'axios';
import { NIL, v4 } from 'uuid';
import { useQueryClient, QueryStatus } from 'react-query';
import { useHistory } from 'react-router-dom';
import IconWorkOrders from '../assets/Icon-WorkOrder.svg';
import { WorkOrder, WorkOrderStatus } from '../models/workOrder';
import useWorkOrder from '../hooks/useWorkOrder';
import useWorkOrdersArchived from '../hooks/useWorkOrdersArchived';
import useCurrentUser from '../hooks/useCurrentUser';
import DocumentGridShell from './DocumentGridShell';
import { useConfirm } from './ConfirmProvider'; // item 1500 — row-level confirm
import LedgerTable, { LedgerColumn, LedgerBulkAction } from './LedgerTable';
import LedgerSubline from './LedgerSubline';
import { LEDGER_W } from './ledgerGrid';
import { isArchivedRow } from '../utils/archived'; // item 714
import StatusPill from './StatusPill';
import GenerateInvoiceModal from './GenerateInvoiceModal';
import WorkOrderLeadsModal from './WorkOrderLeadsModal';
import WorkOrderMoveModal from './WorkOrderMoveModal'; // item 1353d
import openPdf, { previewPdf } from '../utils/openPdf';
import { nextVersionName } from '../utils/proposalNaming';
import { approverOf } from '../utils/approver';
import { SqlDateTimeToDisplay } from '../utils/SqlTools'; // item 1331 — CREATED column
import { canArchiveWorkOrder } from '../utils/archiveRules';
import { useProjectContracts } from '../hooks/useContracts'; // item 1264 — contract-number awareness

// item 553 — Work Orders on the shared LEDGER (mirrors Proposals, item 547) with an AUTOMATIC,
// billing-driven status (Chad): a WO is "In Progress" until it's fully billed, then the final
// (100%) invoice auto-closes it to "Complete" — the backend already does this in
// InvoiceCreateAction (deposit leaves it open; the final/full invoice closes it; voiding the
// last invoice reopens it). So there is NO manual status dropdown — the ⋮ only edits/assigns/
// voids/archives. A WO born from a proposal shows "Tied to Proposal ####" and tracks the same
// invoicing that advances its proposal.

// billing-driven pill, in the proposal's vocabulary: fully-billed (closed) reads "Complete",
// everything else reads "In Progress". StatusPill colours In Progress blue, Complete green.
const isClosed = (s: WorkOrderStatus): boolean => (
  s === WorkOrderStatus.InvoicesGenerated || s === WorkOrderStatus.WorkComplete
);
const pillLabel = (s: WorkOrderStatus): string => (isClosed(s) ? 'Complete' : 'In Progress');

interface WorkOrdersGridProps {
  data?: WorkOrder[];
  status: QueryStatus;
  projectId?: string;
  collapse?: { collapsed: boolean; onToggle: () => void };
  // item 553 — on the project page the WO rows are entities (no flat proposalNumber); pass the
  // project's already-loaded proposals so a tied WO can show "Tied to Proposal ####" without a
  // backend change (the global work-orders page carries proposalNumber on the row itself).
  proposals?: Array<{ id: string; proposalNumber?: string }>;
}

const WorkOrdersGrid: React.FC<WorkOrdersGridProps> = (props) => {
  const {
    data, status, projectId, collapse, proposals,
  } = props;
  const inProject = !!projectId && projectId !== NIL;
  // item 1264 (Chad: "this work order number is linked to contracts — mark it") — inside a
  // project, cross-reference the project's contracts: a WO whose number is written into live
  // contract paper wears a CONTRACT badge, so nobody treats that number as casually editable.
  const { data: projectContracts } = useProjectContracts(inProject ? projectId : undefined);
  const contractCountByNumber = useMemo(() => {
    const m = new Map<string, number>();
    (projectContracts?.index ?? []).forEach((c) => {
      const n = (c.number ?? '').trim().toUpperCase();
      if (n) m.set(n, (m.get(n) ?? 0) + 1);
    });
    return m;
  }, [projectContracts]);
  const proposalNumberById = useMemo(() => {
    const m = new Map<string, string>();
    (proposals ?? []).forEach((p) => { if (p.proposalNumber) m.set(p.id, p.proposalNumber); });
    return m;
  }, [proposals]);
  const proposalNumberOf = (w: WorkOrder): string | undefined => w.proposalNumber
    || (w.proposalId ? proposalNumberById.get(w.proposalId) : undefined);
  // item 1475 — the row's lead line: the WO's own leads (item 526), else the proposal's mirrored
  // leads (legacy deals whose lead was named before leads moved onto the WO)
  const proposalLeadsById = useMemo(() => {
    const m = new Map<string, string[]>();
    (proposals ?? []).forEach((p) => { const names = (p as any).projectLeadNames as string[] | undefined; if (p.id && names?.length) m.set(p.id, names); });
    return m;
  }, [proposals]);
  const leadLineFor = (w: WorkOrder): string | null => {
    let names: string[] = w.leadNames ?? [];
    if (names.length === 0 && w.proposalId) names = proposalLeadsById.get(w.proposalId) ?? [];
    return names.length > 0 ? `Lead: ${names.join(', ')}` : null;
  };
  const {
    tryArchive, tryVoid, voidMany, tryUnvoid, tryUpdate: tryAddWorkOrder, fetchById,
  } = useWorkOrder({ id: NIL });
  const { canModerate, isAdmin } = useCurrentUser(); // item 142 — void/archive = Pulse Group only
  // item 553 — archived WOs are a filter view (like proposals), with a moderator Restore
  const { archived, restore, isRestoring } = useWorkOrdersArchived();
  const history = useHistory();
  const toast = useToast();
  // item 1500 (Chad) — the row ⋮ Void/Archive fired with NO confirm (the bulk bar asks; one
  // row didn't). Same shared dialog + wording as the bulk bar; a WO void is reversible
  // (item 1208 Restore), so say so rather than claim it's permanent.
  const confirm = useConfirm();
  // EPIC_VOID_FLOW_2026-10 — the shared void dialog asks (reason + Notify customer); no pre-confirm
  const confirmVoid = (w: WorkOrder) => { tryVoid(w.id); };
  const confirmArchive = async (w: WorkOrder) => {
    if (!(await confirm(
      `Archive ${w.workOrderNumber || 'this work order'}? It leaves the ledger (the Archived filter still shows it).`,
      { title: 'Archive work order', confirmLabel: 'Archive', destructive: true },
    ))) return;
    tryArchive(w.id);
  };
  const queryClient = useQueryClient(); // item 1348

  const genModal = useDisclosure();
  const [genWoId, setGenWoId] = useState('');
  const leadModal = useDisclosure();
  const [leadWoId, setLeadWoId] = useState('');
  const moveModal = useDisclosure(); // item 1353d
  const [moveWo, setMoveWo] = useState<WorkOrder | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<'all' | 'inprogress' | 'complete' | 'archived'>('all');
  const showingArchived = statusFilter === 'archived';

  const restoreArchived = async (w: WorkOrder) => {
    try {
      await restore(w.id);
      toast({ status: 'success', title: `Restored ${w.workOrderNumber || w.workOrderTitle}.` });
    } catch (e: any) {
      toast({
        status: 'error',
        title: 'Restore failed',
        description: e?.response?.status === 403 ? 'Only an administrator can restore.' : 'Try again.',
      });
    }
  };

  const onPdfError = () => toast({ status: 'error', title: 'Could not open the PDF' });
  const previewWo = (id: string) => previewPdf(`WorkOrder/${id}/pdf`).catch(onPdfError);
  const downloadWo = (id: string) => openPdf(`WorkOrder/${id}/pdf`).catch(onPdfError);
  const tryEdit = (id: string) => history.push(`/workOrder/${id}`);

  const openGenerate = (id: string) => { setGenWoId(id); genModal.onOpen(); };
  const openLeads = (id: string) => { setLeadWoId(id); leadModal.onOpen(); };
  // item 1402 — mint (or re-mint) the customer-portal copy of a sales order
  const sharePortal = async (w: WorkOrder) => {
    try {
      const { data: shared } = await axios.post(`${process.env.REACT_APP_BASIX_API || ''}WorkOrder/${w.id}/portal-share`);
      queryClient.invalidateQueries(['salesOrderPortal', w.id]);
      toast({ status: 'success', title: 'Shared to the customer portal', description: `${shared?.number ?? w.workOrderNumber} is now visible to the customer for approval.` });
    } catch (e: any) {
      toast({
        status: 'error', title: 'Could not share', description: String(e?.response?.data ?? e?.message ?? 'Please try again.'), duration: 8000, isClosable: true,
      });
    }
  };
  const openMove = (w: WorkOrder) => { setMoveWo(w); moveModal.onOpen(); }; // item 1353d

  const duplicateWorkOrder = async (id: string) => {
    try {
      const original = await fetchById(id);
      if (!original) return;
      const copy = { ...original };
      copy.id = NIL;
      // dart (2026-07-15, Chad) — versioned naming ("X" → "X V.1") instead of "Copy of …"
      copy.workOrderTitle = nextVersionName(copy.workOrderTitle ?? '', (data ?? []).map((w) => w.workOrderTitle ?? ''));
      copy.workOrderNumber = '';
      // item 531 — a duplicate is a fresh commission slate (don't inherit the customer PM)
      copy.customerProjectManagerId = undefined;
      copy.workOrderItems.forEach((i) => { i.id = v4(); });
      // item 535 — a duplicate is a BRAND-NEW, editable work order
      copy.status = WorkOrderStatus.Generated;
      copy.isVoid = false;
      copy.isArchived = false;
      copy.isLocked = false;
      await tryAddWorkOrder(copy);
    } catch {
      toast({ status: 'error', title: 'Action failed', description: 'The document was NOT saved.' });
    }
  };

  const columns: LedgerColumn<WorkOrder>[] = useMemo(() => [
    {
      // item 1445 (Chad, after the 1444 invoice fold: "I thought we were doing a pass on all the ledgers") —
      // WORK ORDER = the portal pattern: number bold (+ the item-1264 CONTRACT badge), title as
      // two-line subtext, then deal linkage + customer · project. The separate NUMBER + NAME
      // columns are gone; this is the one flexible column (300px floor). Sort by number.
      // item 1447 — title + "Tied to Proposal" + customer · project ride the full-width rowSubline.
      key: 'number',
      header: 'WORK ORDER',
      width: LEDGER_W.id,
      sortValue: (w) => w.workOrderNumber ?? '',
      render: (w) => {
        const n = (w.workOrderNumber ?? '').trim();
        // item 1264 — the number a contract is written against is legal-paper identity, not a
        // casual field: badge it, and say why on hover.
        const contracts = n ? (contractCountByNumber.get(n.toUpperCase()) ?? 0) : 0;
        return (
          <Box minW={0}>
            <HStack spacing={1.5}>
              <Text fontWeight="semibold" whiteSpace="nowrap">{n || '—'}</Text>
              {contracts > 0 && (
                <Tooltip
                  label={`This is the number we contract with — it's written into ${contracts} contract${contracts === 1 ? '' : 's'} on this project. Changing it breaks the paper trail.`}
                  hasArrow
                  placement="top"
                  openDelay={200}
                >
                  <Badge colorScheme="purple" variant="solid" fontSize="0.55rem" cursor="default">CONTRACT</Badge>
                </Tooltip>
              )}
            </HStack>
          </Box>
        );
      },
    },
    {
      // item 1331 (Chad: "do we have room to add a date") — CREATED date; the ledger's default sort
      // (newest on top) so the chevron has a column to sit on. Number order lies for revisions.
      key: 'created',
      header: 'CREATED',
      width: LEDGER_W.created,
      sortValue: (w) => (w as { createdDate?: string; lastModifiedDate?: string }).createdDate ?? (w as { lastModifiedDate?: string }).lastModifiedDate ?? '',
      render: (w) => {
        const d = (w as { createdDate?: string; lastModifiedDate?: string }).createdDate ?? (w as { lastModifiedDate?: string }).lastModifiedDate;
        return <span style={{ whiteSpace: 'nowrap' }}>{d ? SqlDateTimeToDisplay(d) : '—'}</span>;
      },
    },
    // NAME (title + item-553 subtitle) folded under the number — item 1445.
    // item 559 — APPROVED BY, the same column on every module grid: inherited from the
    // source proposal (client e-signature name, else the internal approver); standalone
    // WOs have no approval chain and show a dash.
    {
      key: 'approvedBy',
      header: 'APPROVED BY',
      width: LEDGER_W.slot3,
      sortValue: (w) => approverOf(w as any) ?? '',
      render: (w) => {
        const name = approverOf(w as any);
        return name
          ? <Text fontSize="sm" noOfLines={1}>{name}</Text>
          : <Text fontSize="sm" opacity={0.4}>—</Text>;
      },
    },
    {
      // item 553 — WO/SO + VOID tags get their own column (work orders don't need an AMOUNT)
      key: 'type',
      header: 'TYPE',
      width: LEDGER_W.slot4,
      sortValue: (w) => (w.isSalesOrder ? 'SO' : 'WO'),
      render: (w) => (
        <HStack spacing={2}>
          <Badge colorScheme={w.isSalesOrder ? 'purple' : 'gray'} fontSize="0.6rem">{w.isSalesOrder ? 'SO' : 'WO'}</Badge>
          {w.isVoid && <Badge colorScheme="red" fontSize="0.6rem">VOID</Badge>}
        </HStack>
      ),
    },
    {
      key: 'status',
      header: 'STATUS',
      width: LEDGER_W.status,
      sortValue: (w) => w.status,
      render: (w) => <StatusPill label={pillLabel(w.status)} isVoid={w.isVoid} minW="118px" />,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [inProject, proposalNumberById, contractCountByNumber]);

  // shared trailing icons, kept aligned on every row (void rows use spacers where an active
  // row has Duplicate / ⋮)
  const iconSpacer = <Box w="24px" flexShrink={0} />;
  const previewBtn = (w: WorkOrder) => (
    <Tooltip label="Preview" openDelay={300}>
      <IconButton size="xs" variant="ghost" aria-label="Preview" icon={<FiEye />} onClick={() => previewWo(w.id)} />
    </Tooltip>
  );
  const downloadBtn = (w: WorkOrder) => (
    <Tooltip label="Download PDF" openDelay={300}>
      <IconButton size="xs" variant="ghost" aria-label="Download PDF" icon={<FiDownload />} onClick={() => downloadWo(w.id)} />
    </Tooltip>
  );

  const rowActions = (w: WorkOrder): React.ReactNode => {
    // item 553 — Archived filter view: Restore in the primary slot (blue) + Preview/Download.
    if (showingArchived) {
      return (
        <HStack spacing={1} justify="flex-end">
          {canModerate && <Button size="xs" minW="76px" colorScheme="blue" isLoading={isRestoring} onClick={() => restoreArchived(w)}>Restore</Button>}
          {iconSpacer}
          {previewBtn(w)}
          {downloadBtn(w)}
          {iconSpacer}
        </HStack>
      );
    }
    // item 553 — a VOID work order STAYS in the list; Restore takes the primary slot (blue),
    // mirroring proposals; Preview/Download keep their columns.
    if (w.isVoid) {
      return (
        <HStack spacing={1} justify="flex-end">
          {canModerate && <Button size="xs" minW="76px" colorScheme="blue" onClick={() => tryUnvoid(w.id)}>Restore</Button>}
          {iconSpacer}
          {previewBtn(w)}
          {downloadBtn(w)}
          {iconSpacer}
        </HStack>
      );
    }
    const canInvoice = !isArchivedRow(w);
    // item 561 — once ANY live invoice exists (a deposit leaves the WO's status open, so the
    // flag counts invoices), the primary slot reads INVOICED and further invoicing moves to
    // ⋮ → Create another invoice (backend allows re-invoicing since item 231).
    const invoiced = (w as any).hasLiveInvoice === true;
    return (
      <HStack spacing={1} justify="flex-end">
        {canInvoice && !invoiced && <Button size="xs" minW="76px" colorScheme="brandGreen" onClick={() => openGenerate(w.id)}>Invoice</Button>}
        {canInvoice && invoiced && (
          <Badge colorScheme="green" variant="subtle" minW="76px" textAlign="center" fontSize="0.6rem" py={1}>
            INVOICED
          </Badge>
        )}
        <Tooltip label="Duplicate" openDelay={300}>
          <IconButton size="xs" variant="ghost" aria-label="Duplicate" icon={<FiCopy />} onClick={() => duplicateWorkOrder(w.id)} />
        </Tooltip>
        {previewBtn(w)}
        {downloadBtn(w)}
        <Menu isLazy>
          <MenuButton as={IconButton} size="xs" variant="ghost" aria-label="More actions" icon={<FiMoreVertical />} />
          <Portal>
            <MenuList fontSize="sm">
              {/* item 553 — status is AUTOMATIC (billing-driven); no manual status changes here */}
              <MenuItem icon={<FiEdit2 />} onClick={() => tryEdit(w.id)}>Edit</MenuItem>
              {canInvoice && invoiced && (
                <MenuItem icon={<FiFileText />} onClick={() => openGenerate(w.id)}>Create another invoice</MenuItem>
              )}
              {/* item 1402 — a SALES ORDER can be shared to the customer portal from the ledger
                  (mints the frozen portal copy; re-share supersedes). Never offered on a WO. */}
              {w.isSalesOrder && !w.isVoid && !isArchivedRow(w) && (
                <MenuItem icon={<FiExternalLink />} onClick={() => sharePortal(w)}>Share to customer portal</MenuItem>
              )}
              {isAdmin && <MenuItem icon={<FiUsers />} onClick={() => openLeads(w.id)}>Assign project lead…</MenuItem>}
              {/* item 1353d — same customer only, invoices follow, works on locked WOs */}
              {isAdmin && <MenuItem icon={<FiFolder />} onClick={() => openMove(w)}>Move to another project…</MenuItem>}
              {/* item 1352 (Chad: "I need an option for work orders to allow a reopen — the
                  invoices are where we have the hard gate") — REOPEN unlocks a closed WO for
                  edits/re-invoicing; the 1348 auto-close re-fires at the next fully-billed
                  finalize, and duplicating stays available for a fresh copy. */}
              {canModerate && isClosed(w.status) && (
                <MenuItem
                  icon={<FiRefreshCw />}
                  onClick={async () => {
                    try {
                      const apiUrl = process.env.REACT_APP_BASIX_API || '';
                      await axios.post(`${apiUrl}workorder/SetWoStatus/${w.id}`, 1, { headers: { 'content-type': 'application/json' } });
                      queryClient.invalidateQueries('workOrders');
                      toast({ status: 'success', title: `${w.workOrderNumber ?? 'Work order'} reopened.`, description: 'Editable again — it re-closes automatically when fully billed.' });
                    } catch (e: any) {
                      toast({ status: 'error', title: 'Could not reopen', description: String(e?.response?.data ?? 'Try again.') });
                    }
                  }}
                >
                  Reopen (unlock)
                </MenuItem>
              )}
              {/* item 1348 (Chad: "I will be able to complete work orders") — the manual settle
                  for migrations/externally-billed jobs; native flows auto-close at $0 left. */}
              {canModerate && !isClosed(w.status) && (
                <MenuItem
                  icon={<FiCheckCircle />}
                  onClick={async () => {
                    try {
                      const apiUrl = process.env.REACT_APP_BASIX_API || '';
                      await axios.post(`${apiUrl}workorder/SetWoStatus/${w.id}`, 3, { headers: { 'content-type': 'application/json' } });
                      queryClient.invalidateQueries('workOrders');
                      toast({ status: 'success', title: `${w.workOrderNumber ?? 'Work order'} marked Complete.` });
                    } catch (e: any) {
                      toast({ status: 'error', title: 'Could not complete', description: String(e?.response?.data ?? 'Try again.') });
                    }
                  }}
                >
                  Mark complete (fully billed)
                </MenuItem>
              )}
              {canModerate && <MenuDivider />}
              {/* item 1500 — confirm first, exactly like the bulk bar */}
              {canModerate && <MenuItem onClick={() => confirmVoid(w)}>Void</MenuItem>}
              {canModerate && canArchiveWorkOrder(w) && <MenuItem onClick={() => confirmArchive(w)}>Archive</MenuItem>}
            </MenuList>
          </Portal>
        </Menu>
      </HStack>
    );
  };

  // checkboxes → BULK housekeeping only (archive / void); never bulk invoice
  const bulkActions: LedgerBulkAction<WorkOrder>[] = canModerate ? [
    {
      label: 'Archive',
      appliesTo: (w) => !isArchivedRow(w) && canArchiveWorkOrder(w), // item 1570 — void first
      confirm: (rows) => `Archive ${rows.length} work order(s)?`,
      onRun: async (rows) => { await Promise.all(rows.map((w) => tryArchive(w.id))); },
    },
    {
      label: 'Void',
      colorScheme: 'red',
      appliesTo: (w) => !w.isVoid,
      onRun: async (rows) => { await voidMany(rows.map((w) => w.id)); }, // EPIC_VOID_FLOW — one dialog (reason + notify)
    },
  ] : [];

  const rows = useMemo(() => {
    if (statusFilter === 'archived') {
      return (archived as WorkOrder[]).filter((w) => !inProject || w.projectId === projectId);
    }
    const list = (data ?? []).filter((w) => !isArchivedRow(w));
    if (statusFilter === 'complete') return list.filter((w) => isClosed(w.status));
    if (statusFilter === 'inprogress') return list.filter((w) => !isClosed(w.status));
    return list;
  }, [data, statusFilter, archived, inProject, projectId]);

  const genWorkOrder = (data ?? []).find((d) => d.id === genWoId);
  const leadWorkOrder = (data ?? []).find((d) => d.id === leadWoId);

  return (
    <>
      <DocumentGridShell
        heading="SALES & WORK ORDERS"
        icon={IconWorkOrders}
        addPath={`/workOrder/${NIL}${inProject ? `?projectId=${projectId}` : ''}`}
        collapsible={!!collapse}
        collapsed={collapse?.collapsed}
        onToggleCollapse={collapse?.onToggle}
        status={status}
        // item 712 (Chad) — header count must match the visible (archived-filtered) rows
        data={data === undefined ? undefined : rows}
      >
        <LedgerTable<WorkOrder>
          defaultSort={{ key: 'created', dir: -1 }} // item 1331 (Chad) — DATE order, newest on top (number order lies for revisions)
          rows={rows}
          columns={columns}
          rowSubline={(w) => (
            // item 553's deal linkage + customer · project, now on the full-width line (item 1447)
            <LedgerSubline
              name={w.workOrderTitle}
              meta={[
                w.proposalId ? `Tied to Proposal ${proposalNumberOf(w) ?? ''}`.trim() : null,
                inProject ? null : (w as any).customerName,
                inProject ? null : (w as any).projectName,
                // item 1475 (Chad) — who is running the job, on the row (WO leads, else the proposal's)
                leadLineFor(w),
              ]}
            />
          )}
          rowId={(w) => w.id}
          rowActions={rowActions}
          rowActionsWidth={LEDGER_W.actions}
          rowActionsFlex
          rowActionsHeader="ACTION"
          bulkActions={showingArchived ? [] : bulkActions}
          selectable={canModerate && !showingArchived}
          filters={(
            <Select size="sm" maxW="200px" variant="filled" borderRadius="lg" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}>
              <option value="all">ALL STATUSES</option>
              <option value="inprogress">IN PROGRESS</option>
              <option value="complete">COMPLETE</option>
              {/* item 553 — archived work orders as a filter view, not a peer section */}
              <option value="archived">ARCHIVED</option>
            </Select>
          )}
          searchText={(w) => `${w.workOrderNumber ?? ''} ${w.workOrderTitle ?? ''} ${(w as any).customerName ?? ''}`}
          searchPlaceholder="Search work orders…"
          onRowClick={showingArchived ? undefined : (w) => tryEdit(w.id)}
          emptyText={showingArchived ? 'No archived work orders.' : 'No work orders yet.'}
        />
      </DocumentGridShell>
      <GenerateInvoiceModal
        isOpen={genModal.isOpen}
        onClose={genModal.onClose}
        workOrderId={genWoId}
        isSalesOrder={genWorkOrder?.isSalesOrder}
        fromProposal={!!genWorkOrder?.proposalId}
      />
      <WorkOrderMoveModal workOrder={moveWo} isOpen={moveModal.isOpen} onClose={moveModal.onClose} />
      {leadWoId ? (
        <WorkOrderLeadsModal
          isOpen={leadModal.isOpen}
          onClose={leadModal.onClose}
          workOrderId={leadWoId}
          workOrderNumber={leadWorkOrder?.workOrderNumber}
        />
      ) : null}
    </>
  );
};

WorkOrdersGrid.defaultProps = {
  data: undefined,
  projectId: NIL,
  collapse: undefined,
  proposals: undefined,
};

export default WorkOrdersGrid;
